/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {SurveyCategoryLookup} from '../models/survey-category.model';
import {PlanPurposeLookup} from '../models/plan-purpose.model';
import {PlanTypeLookup} from '../models/plan-type.model';
import {FormerTenureSubjectOfPlan} from '../models/former-tenure-subject-of-plan.model';
import {v4 as uuid4} from 'uuid';
import {PDF_CREATION_METHOD, PLAN_LODGEMENT_STAGE, VALIDATION_EVENT_TYPE} from '../constants';
import {SurveyMethod} from '../models/survey-method.model';
import {PlanningAuthorityReference} from '../models/planning-authority-reference.model';
import {CompiledFromReference, compiledFromSettings} from '../models/compiled-from-reference-model';
import {CrownStateLand} from '../models/crown-state-land-model';
import {AuthenticatorService} from './authenticator.service';
import {equalsIgnoreAttrsValues, isObject} from './data-util';
import {catchError, map, Observable, of, tap, throwError} from 'rxjs';
import {StrataScheme} from '../models/strata-scheme.model';
import {NgEventBus} from 'ng-event-bus';
import {PlanLocality} from '../models/plan-locality.model';
import {PlanLga} from '../models/plan-lga.model';
import {Locality} from '../models/locality.model';
import {AppConfigService} from './app-config.service';
import {CertificationType} from '../models/certification-type.model';
import {Certification} from '../models/certification.model';
import {DependantPlan} from '../models/dependant-plan.model';
import {StrataPlanAction} from '../models/strata-plan-action.model';
import {PlanningApproval} from '../models/planning-approval-model';
import {AggregateQuestionResponse} from '../models/aggregate-question.response';
import {PlanAttachment} from '../models/plan-attachment.model';
import {Fee} from '../models/fee.model';
import {Customer} from '../models/customer.model';
import * as JSOG from '../../../../dependencies/jsog-1.0.7/JSOG';
import {SpatialSettings} from '../models/spatial-settings.model';
import {PlanRoad} from '../models/plan-road.model';
import {switchMap} from 'rxjs/operators';
import {FieldValidation, ValidationService} from './validation.service';
import {DigitalPlanAttachment} from '../../digital-plan/digital-plan.model';

export interface SurveyingFirm {
    firmNumber?: number;
    firmName?: string;
    mailAddress?: string;
    locality?: string;
    postcode?: string;
    phoneNumber?: string;
    mobilePhoneNumber?: string;
    facsimileNumber?: string;
    email?: string;
}

export interface Surveyor {
    surveyorIdNumber: number;
    familyName: string;
    givenNames: string;
    nameLabel: string;
    registrationNumber: number;
    mailAddress: string;
    locality: string;
    postcode: string;
    phone: string;
    mobile: string;
    facsimileNumber: string;
    email: string;
    defaultFirmNumber: number;
}

//TODO replace all 'any' types with implementation of interface
export interface PlanModel {
    formerTenureSubjectOfPlan?: FormerTenureSubjectOfPlan;
    certifiedBy?: any;
    strata?: StrataScheme;
    attempt?: number;
    creator?: string;
    creatorName?: string;
    assignedTo?: string;
    assignedToName?: string;
    lodgementType?: {value: string};
    publishedRevision?: number;
    id?: number;
    guid?: string;
    planLodgementStage?: string;
    planStage?: number;
    pdfCreationMethod?: string;
    event?: string;
    tenureType?: SurveyCategoryLookup;
    planPurpose?: PlanPurposeLookup;
    planType?: PlanTypeLookup;
    formerTenureSubject?: FormerTenureSubjectOfPlan;
    planNumber?: number;
    planHeading?: string;
    customerReference?: string;
    formerTenures?: any[];
    localities?: PlanLocality[];
    localGovernmentAuthorities?: PlanLga[];
    newInterestSameOwner?: any;
    interests?: any;
    versionNumber?: number;
    surveyMethod?: SurveyMethod;
    certificationType?: CertificationType;
    certification?: Certification;
    certifyingSurveyor?: Surveyor;
    spatialSettings?: SpatialSettings;
    ssa?: any;
    newOrExtendedRoads?: any;
    roads?: PlanRoad[];
    attachments?: PlanAttachment[];
    approvalExemption?: boolean;
    exemptedRoads?: {name?: string}[];
    roadWidening?: boolean;
    lgaApprovedUnnamedRoad?: boolean;
    laaAcquisition?: any;
    roadClosure?: any;
    planningAuthorityReferences?: PlanningAuthorityReference[];
    planningAuthority?: PlanningApproval;
    crownStateLand?: CrownStateLand;
    insetLots?: any;
    numberOfInsetLots?: number;
    dependantPlans?: DependantPlan[];
    strataPlanActions?: StrataPlanAction[];
    fieldRecords?: any[];
    compiledFromReferences?: CompiledFromReference[];
    firm?: SurveyingFirm;
    surveyor?: Surveyor;
    totalDue?: any;
    fees?: Fee[];
    lodgementEmailAddress?: string;
    spatialDependencyCalcDateTime?: string;
    lodgeType: string;
    purpose: string;
    lodgementReceiptEmailAddress: string;
    category: string;
    geodeticConnections?: {markType?: string; name?: string}[];
    specialSurveyArea?: boolean;
    ssmGeodeticConnections?: [];
    unnamedGeodeticConnection?: boolean;
    benchmarkConnections?: [];
    amendmentDetails?: string;
    certifyingSurveyorIdNumber?: number;
    surveyorIdNumber?: number;
    address?: {locality: Locality};
    subjectPlans?: [];
    amendmentDescription?: string;
    generalComments?: string;
    planConformanceAgreement?: boolean;
    planPdfReflectsDigitalCapturedData?: boolean;
    planReleaseConsent?: boolean;
    aggregateInterestQuestions: AggregateQuestionResponse[];
    adviceName?: string;
    adviceComment?: string;
    adviceDate?: string | number; //ISO date string (yyyy-MM-dd) on input, timestamp number after save / reload
    chargeablePlanLots?: number;
    planLots?: number;
    lotSync?: boolean;
    customer?: Customer;
    applicationForTitlesDocumentIdentifier?: string;
    partiallySurrenderedInterests?: boolean;
    lodgementDateString?: string;
    digitalPlanAttachment?: DigitalPlanAttachment;
}

@Injectable({
    providedIn: 'root'
})
export class PlanModelService {
    isNotLandgateSigned: boolean;
    constructor(
        private http: HttpClient,
        private eventBus: NgEventBus,
        private authenticatorService: AuthenticatorService,
        private appConfigService: AppConfigService,
        private validationService: ValidationService
    ) {
        this.initPlanModel();
    }
    readonly = false;
    populateFormerTenureNotifications: boolean;
    model: PlanModel;
    title = 'Plan Model Service';
    modelForSerialisation = {};
    pristineModel: Partial<PlanModel> = {};
    pristineModelForSerialisation = {};
    validated?: boolean;

    createGuid() {
        return uuid4();
    }

    getModel() {
        return this.model;
    }

    initPlanModel() {
        this.model = {
            guid: this.createGuid(),
            planLodgementStage: PLAN_LODGEMENT_STAGE.IN_DESIGN,
            pdfCreationMethod: PDF_CREATION_METHOD.CAD_SOFTWARE,
            firm: {},
            lodgeType: '',
            purpose: '',
            spatialSettings: {
                projectedCoordSystem: undefined
            },
            event: VALIDATION_EVENT_TYPE.INFORMATION,
            attempt: 0,
            versionNumber: 1,
            lodgementType: {value: 'New'},
            planStage: 1,
            ssa: false,
            localities: [],
            localGovernmentAuthorities: [{lgaName: '', id: undefined}],
            fieldRecords: [{fieldRecordNumber: ''}],
            roads: [],
            exemptedRoads: [{}],
            compiledFromReferences: [],
            planningAuthorityReferences: [],
            creator: this.authenticatorService.getUsername(),
            creatorName: this.authenticatorService.getDisplayName(),
            assignedTo: this.authenticatorService.getUsername(),
            assignedToName: this.authenticatorService.getDisplayName(),
            publishedRevision: 1,
            lodgementReceiptEmailAddress: '',
            category: '',
            ssmGeodeticConnections: [],
            benchmarkConnections: [],
            geodeticConnections: [],
            aggregateInterestQuestions: [],
            approvalExemption: null,
            interests: [],
            strata: {} as StrataScheme
        };
    }
    sanitiseModel(o) {
        const modelToSanitise = structuredClone(o);
        // sanitise new roads
        if (modelToSanitise.roads) {
            modelToSanitise.roads.splice(
                0,
                modelToSanitise.roads.length,
                ...modelToSanitise.roads.filter(
                    (v, i, a) => v.road && v.road.displayName && i === a.findIndex(w => w.road && w.road.displayName === v.road.displayName)
                )
            );
        }

        if (modelToSanitise.exemptedRoads) {
            // sanitise exempted roads
            modelToSanitise.exemptedRoads.splice(
                0,
                modelToSanitise.exemptedRoads.length,
                ...modelToSanitise.exemptedRoads.filter((v, i, a) => v.name && i === a.findIndex(w => w.name === v.name))
            );
        }

        // sanitise localities
        if (modelToSanitise.localities) {
            let localities = modelToSanitise.localities.filter(locality => locality.localityName);
            localities = localities.length ? localities : [{localityName: ''}];
            modelToSanitise.localities.splice(0, modelToSanitise.localities.length, ...localities);
        }

        // sanitise dependent plans
        if (modelToSanitise.dependantPlans) {
            modelToSanitise.dependantPlans.filter(v => v.validActions).forEach(v => delete v.validActions);
        }

        // sanitise strata plan actions
        if (modelToSanitise.strataPlanActions) {
            modelToSanitise.strataPlanActions.filter(v => v.validActions).forEach(v => delete v.validActions);
        }

        // sanitise spatial settings
        if (
            modelToSanitise.spatialSettings &&
            modelToSanitise.spatialSettings.projectedCoordSystem &&
            modelToSanitise.spatialSettings.projectedCoordSystem.spatialReference
        ) {
            delete modelToSanitise.spatialSettings.projectedCoordSystem.spatialReference;
        }

        //ND-587 Unnecessary saving plan prompt issue. Sanitizing undefined variationApprovals to empty array.
        if (modelToSanitise.certification) {
            if (modelToSanitise.certification.variationApprovals !== undefined && modelToSanitise.certification.variationApprovals.length !== 0) {
                const filteredVariationApprovals = [];
                modelToSanitise.certification.variationApprovals.forEach(approval => {
                    if (
                        approval.variationType !== null &&
                        approval.variationType !== '' &&
                        approval.variationType !== undefined &&
                        approval.regulatoryStatement !== ''
                    ) {
                        filteredVariationApprovals.push(approval);
                    }
                });
                modelToSanitise.certification.variationApprovals = filteredVariationApprovals;
            }
        }

        //ND-587 Unnecessary saving plan prompt issue. Changing calculated fee data types from string to float.
        if (modelToSanitise.fees && modelToSanitise.fees.length > 0) {
            modelToSanitise.fees.forEach(f => {
                f.total = parseFloat(f.total);
                f.baseAmount = parseFloat(f.baseAmount);
            });
        }

        // PAU-1705 Remove unnecessary warning message
        if (modelToSanitise.strata?.parcelAddressRoadName2?.displayName === '') {
            delete modelToSanitise.strata.parcelAddressRoadName2.roadTypeDescription;
            delete modelToSanitise.strata.parcelAddressRoadName2.roadSuffixDescription;
        }

        if (
            modelToSanitise.strata?.parcelAddressRoadName?.displayName &&
            modelToSanitise.strata?.parcelAddressRoadName?.roadTypeDescription === undefined &&
            modelToSanitise.strata?.parcelAddressRoadName?.roadSuffixDescription === undefined
        ) {
            modelToSanitise.strata.parcelAddressRoadName.roadTypeDescription = '';
            modelToSanitise.strata.parcelAddressRoadName.roadSuffixDescription = '';
        }

        // WPS-271 Fixes the issue where users couldn’t update the road name in Scheme details section
        // when the strata had no display name but did have a road name in the database.
        if (modelToSanitise.strata?.parcelAddressRoadName?.displayName) {
            delete modelToSanitise.strata.parcelAddressRoadName.roadName;
            delete modelToSanitise.strata.parcelAddressRoadName.roadTypeCode;
            delete modelToSanitise.strata.parcelAddressRoadName.roadTypeDescription;
        }

        if (modelToSanitise.strata?.parcelAddressRoadName2?.displayName) {
            delete modelToSanitise.strata.parcelAddressRoadName2.roadName;
            delete modelToSanitise.strata.parcelAddressRoadName2.roadTypeCode;
            delete modelToSanitise.strata.parcelAddressRoadName2.roadTypeDescription;
        }

        return modelToSanitise;
    }

    updateModelAttributes(src, keys) {
        const keysInOrder = [
            'tenureType',
            'planType',
            'planNumber',
            'planPurpose',
            'planLodgementStage',
            'planStage',
            'lodgementType',
            'dependantPlans',
            'strataPlanActions',
            'spatialDependencyCalcDateTime',
            'formerTenures',
            'spatialSettings'
        ];

        if (!keys) {
            // Find index of first key where model[k] not equal to src[k], retain keys from that index onwards
            const keysInOrderIdx = keysInOrder.findIndex(k => this.model[k] !== src[k]);
            const keysInOrderToKeep = keysInOrderIdx === -1 ? [] : keysInOrder.slice(Math.min(keysInOrderIdx, keysInOrder.indexOf('formerTenures')));

            // Determine keys which do not need to be reset
            const keysToExcludeFromReset = keysInOrder.filter(k => !keysInOrderToKeep.includes(k));

            // Update keysInOrder with only those keeps that need to be updated after timeout
            keysInOrder.splice(0, keysInOrder.length, ...keysInOrderToKeep);

            if (['tenureType', 'planType', 'planNumber', 'planPurpose'].every(k => keysToExcludeFromReset.includes(k))) {
                // If tenureType, planType, planNumber, planPurpose haven't changed, no need to reset other attributes if they haven't changed
                if (
                    ((src.ssa === 'false' || src.ssa === false) && (this.model.ssa === 'false' || this.model.ssa === false)) ||
                    ((src.ssa === 'true' || src.ssa === true) && (this.model.ssa === 'true' || this.model.ssa === true))
                ) {
                    keysToExcludeFromReset.push('ssa');
                }
                keysToExcludeFromReset.push(
                    ...['planLots', 'planHeading', 'surveyMethod', 'fieldRecords', 'crownStateLand', 'planningAuthority'].filter(
                        k => this.model[k] === src[k]
                    )
                );
            }

            const partialModelForReset = {
                surveyor: undefined,
                firm: undefined,
                tenureType: undefined,
                planType: undefined,
                planNumber: undefined,
                planPurpose: undefined,
                planLots: undefined,
                planHeading: undefined,
                planningAuthority: undefined,
                ssa: false,
                crownStateLand: undefined,
                surveyMethod: undefined,
                fieldRecords: [{fieldRecordNumber: ''}],
                dependantPlans: [],
                strataPlanActions: []
            };

            // Strip out attributes from partialModelForReset that don't require a reset
            keysToExcludeFromReset.filter(k => partialModelForReset.hasOwnProperty(k)).forEach(k => delete partialModelForReset[k]);

            // Assign partialModelForReset to model
            Object.assign(this.model, partialModelForReset);

            // Some attributes need to be updated immediately to ensure model is in a valid state for saves triggered via watchers
            const keysToUpdateImmediately = ['surveyor', 'firm', 'planningAuthority'];
            keysToUpdateImmediately.filter(k => src.hasOwnProperty(k)).forEach(k => (this.model[k] = src[k]));

            keys = keysInOrder.slice();
        }

        if (keys.length === 0) {
            Object.keys(src)
                .filter(k => !keysInOrder.includes(k))
                .forEach(k => (this.model[k] = src[k]));
            return;
        }

        const k = keys.shift();
        this.model[k] = src[k];
        this.updateModelAttributes(src, keys);
    }

    setModel(newModel): PlanModel {
        this.initPlanModel();
        this.updateModelAttributes(newModel, null);
        this.updatePristineModel();
        this.eventBus.cast('nlr:plan-model-updated:model', 'model');
        return this.model;
    }

    savePlan() {
        console.debug(`${this.title} - savePlan`);
        this.sanitizeCompiledFromFields();
        const url = `${this.appConfigService.settings.apiGatewayUrl}/plans/lodgement/save`;
        this.model.guid = this.model.guid || this.createGuid();
        const modelToSave = this.getModelForSerialisation();
        return this.http.post(url, modelToSave).pipe(map(data => JSOG.decode(data)));
    }

    updatePristineModel() {
        this.pristineModel = this.sanitiseModel(this.model);
    }

    /**
     * Refresh attachments in the pristine model. Used when attachments are loaded independently.
     */
    updateAttachments() {
        console.debug('Updating the pristine model attachments');
        this.pristineModel.attachments = structuredClone(this.model.attachments);
    }

    /**
     * ND-587: Unnecessary saving plan prompt issue. Refresh fees in the pristine model. Used when fees are calculated independently.
     */
    updateFees() {
        console.debug('Updating the pristine model attachments');
        this.pristineModel.fees = structuredClone(this.model.fees);
    }

    /**
     * ND-587: Unnecessary saving plan prompt issue. Refreshing totalDue string figure and propagating it into pristine model.
     * @param totalDueFigure
     */

    updateTotalFees(totalDueFigure) {
        console.debug('Updating the model and pristine model totalDue');
        this.model.totalDue = totalDueFigure;
        this.updatePristineModel();
    }

    /**
     * ND-587 Unnecessary saving plan prompt issue. Adding lodgement email to model and propagate the change to pristine model.
     * @param email
     */

    addLodgementEmail(email) {
        this.model.lodgementEmailAddress = email;
        this.updatePristineModel();
    }

    /**
     * Get model guid
     * @returns string
     */
    getGuid() {
        return this.model.guid;
    }

    initGuid() {
        this.model.guid = this.createGuid();
    }

    /**
     * Get coordinate system
     * @returns object
     */
    getCoordinateSystem() {
        return (
            this.model.spatialSettings.projectedCoordSystem || {
                id: 1,
                wkid: this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_SPATIAL_REFERENCE_PLANAR
            }
        );
    }

    /**
     * Get spatial reference
     * @returns object
     */
    getSpatialReference() {
        const coordSystem = this.model.spatialSettings.projectedCoordSystem;
        if (coordSystem) {
            if (coordSystem.wkid) {
                return {wkid: coordSystem.wkid};
            }
            if (coordSystem.wkt) {
                return {wkt: coordSystem.wkt};
            }
        }
        return {wkid: this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_SPATIAL_REFERENCE_PLANAR};
    }

    /**
     * Get sanitised model
     * @returns PlanLodgement
     */
    getSanitisedModel() {
        return this.sanitiseModel(this.model);
    }

    /**
     * Get model for serialisation
     * @returns Object
     */
    getModelForSerialisation() {
        const sanitisedModel = this.sanitiseModel(this.model);
        const newModelForSerialisation = JSOG.encode(structuredClone(sanitisedModel));
        if (this.modelForSerialisation !== newModelForSerialisation) {
            this.modelForSerialisation = newModelForSerialisation;
        }
        return this.modelForSerialisation;
    }

    /**
     * Get pristine model
     * @returns PlanLodgement
     */
    getPristineModel() {
        return this.pristineModel;
    }

    /**
     * Get pristine model for serialisation
     * @returns Object
     */
    getPristineModelForSerialisation() {
        const sanitisedModel = this.sanitiseModel(this.pristineModel);
        const newModelForSerialisation = JSOG.encode(structuredClone(sanitisedModel));
        if (this.pristineModelForSerialisation !== newModelForSerialisation) {
            this.pristineModelForSerialisation = newModelForSerialisation;
        }
        return this.pristineModelForSerialisation;
    }

    /**
     * Merges the two models, preserving references to top-level attributes for the sake of their watchers.
     * Deletes attributes in the target model if they are not present in the source model
     */
    updateModel(planLodgement) {
        planLodgement = structuredClone(planLodgement);
        const srcKeys = Object.keys(planLodgement);

        // Delete attributes on the target model that our updated copy of the model no longer has
        Object.keys(this.model)
            .filter(k => !srcKeys.includes(k))
            .forEach(k => delete this.model[k]);

        // Treat top-level attribute values in a way that preserves the target model's references to them
        // The references to 2nd, 3rd... level attributes will be changed, however
        Object.keys(planLodgement).forEach(k => {
            const v = planLodgement[k];
            const targetModelValue = this.model[k];
            if (Array.isArray(targetModelValue) && Array.isArray(v)) {
                // arrays
                targetModelValue.splice(0, targetModelValue.length, ...v);
            } else if (isObject(targetModelValue) && isObject(v)) {
                // objects (non-arrays)
                const vKeys = Object.keys(v);
                // Delete attributes on the target model that our updated copy of the model no longer has
                Object.keys(targetModelValue)
                    .filter(tmvKey => !vKeys.includes(tmvKey))
                    .forEach(tmvKey => delete targetModelValue[tmvKey]);
                Object.assign(targetModelValue, v);
            } else {
                // primitives
                this.model[k] = v;
            }
        });

        this.updatePristineModel();

        return this.model;
    }

    /**
     * Is model dirty?
     * @returns Boolean
     */
    modelIsDirty() {
        const modelJsog = JSOG.encode(this.sanitiseModel(this.model));
        const pristineModelJsog = JSOG.encode(structuredClone(this.pristineModel));

        return !equalsIgnoreAttrsValues(
            modelJsog,
            pristineModelJsog,
            ['attempt', 'spatialDependencyCalcDateTime', 'variationApprovals', '@id'],
            [null, undefined]
        );
    }

    /**
     * Load plan lodgement
     * @param planLodgementId The plan lodgement id
     * @returns Observable<PlanLodgement>
     */
    load(planLodgementId) {
        const logPrefix = `${this.title} - load - planLodgementId=${planLodgementId}`;
        console.debug(logPrefix);
        this.validated = false;
        return this.http
            .get(`${this.appConfigService.settings.apiGatewayUrl}/plans/lodgement/loadPlan/${planLodgementId}`, {transferCache: false})
            .pipe(
                map(data => {
                    console.info(`${logPrefix} - success`);
                    return this.setModel(JSOG.decode(data));
                }),
                catchError(error => {
                    console.error(`${logPrefix} - error:`, error.message);
                    return of([]);
                })
            );
    }

    /**
     * Load new version of plan lodgement
     * @param planLodgementId The plan lodgement id
     * @param lodgementType The lodgement type
     * @returns Observable<PlanLodgement>
     */
    loadNewVersion(planLodgementId, lodgementType): Observable<any> {
        console.info(`${this.title} - loadNewVersion planLodgementId=${planLodgementId} lodgementType=${lodgementType}`);

        const newGuid = this.createGuid();
        const url =
            this.appConfigService.settings.apiGatewayUrl +
            '/plans/lodgement/createNewPlanVersion/' +
            planLodgementId +
            '/' +
            lodgementType.toLowerCase() +
            '/' +
            newGuid;

        return this.http.get<any>(url, {transferCache: false}).pipe(
            map(data => this.setModel(JSOG.decode(data))),
            catchError(error => {
                return throwError(() => {
                    new Error(error);
                });
            })
        );
    }

    /**
     * Save the plan lodgement
     */
    save(): Observable<PlanModel> {
        console.debug(`${this.title} - save`);
        return this.savePlan().pipe(
            map(data => {
                this.updateModel(data);
                return this.model;
            }),
            tap(() => console.debug(`${this.title} - save success!`)),
            catchError(error => {
                const errorMessage = `Error saving plan: ${error.message}`;
                console.error(errorMessage);
                throw errorMessage;
            })
        );
    }

    /**
     * Save and reload the plan lodgement
     * @returns Promise<PlanLodgement>
     */
    //TODO Left as a commented method for now. This method is not required
    // saveAndReload() {
    //     console.debug(`${this.title} - saveAndReload`);
    //     return this.savePlan().subscribe({
    //         next: data => {
    //             console.debug(`${this.title} - save success!`);
    //             this.setModel(data);
    //         },
    //         error: error => {
    //             console.error('Error saving plan ' + error.message);
    //         }
    //     });
    // }

    /**
     * Assign the plan lodgement
     * @param userName ??
     * @param assignedToName ??
     * @param planLodgementId The plan lodgement id
     * @returns Promise<PlanLodgement>
     */
    assign(userName: string, assignedToName: string, planLodgementId: any): Observable<{}> {
        planLodgementId = planLodgementId ? parseInt(planLodgementId) : this.model.id;
        console.debug(`${this.title} - assign userName=${userName}, assignedToName=${assignedToName}, planLodgementId=${planLodgementId}`);

        return this.http
            .post(`${this.appConfigService.settings.apiGatewayUrl}/plans/lodgement/assignUser`, {
                planLodgementId: planLodgementId,
                assignedTo: userName,
                assignedToName: assignedToName
            })
            .pipe(tap(res => this.updateModel(JSOG.decode(res))));
    }

    /**
     * Transition stage
     * @param planLodgementStage The plan lodgement stage
     * @returns Promise<PlanLodgement>
     */
    transitionStage(planLodgementStage) {
        console.debug(`${this.title} - transitionStage planLodgementStage=${planLodgementStage}`);
        return this.http
            .post(`${this.appConfigService.settings.apiGatewayUrl}/plans/lodgement/transitionStage`, {
                planLodgementId: this.model.id,
                planLodgementStage: planLodgementStage
            })
            .pipe(
                switchMap(data => {
                    console.debug(`${this.title} - transitionStage success!`);
                    this.updateModel(JSOG.decode(data));
                    return of(null);
                }),
                catchError(error => {
                    console.error(`${this.title} - transitionStage error:`, error.message);
                    return of(null);
                })
            );
    }

    /**
     * Lodge the plan
     * @returns Promise<PlanLodgement>
     */
    lodge() {
        console.debug(`${this.title} - lodge`);
        const modelToLodge = this.getModelForSerialisation();
        return this.http.post(`${this.appConfigService.settings.apiGatewayUrl}/lodgement/lodge`, modelToLodge).pipe(
            map(data => {
                console.debug(`${this.title} - lodge success!`);
                return this.updateModel(JSOG.decode(data));
            }),
            catchError(error => {
                console.error(`${this.title} - lodge error:`, error);
                return throwError(() => new Error('Error lodging plan'));
            })
        );
    }

    sanitizeVariationApprovals() {
        const variationApprovals = this.model.certification.variationApprovals;
        variationApprovals.splice(0, variationApprovals.length, ...variationApprovals.filter(variationApproval => variationApproval.variationType));
    }

    sanitizeCompiledFromFields() {
        this.model.compiledFromReferences.forEach((reference: CompiledFromReference) => {
            const filteredSettings = compiledFromSettings.filter(s => s.filterValue === reference.source);
            if (filteredSettings.length != 1) {
                return;
            }
            const setting = filteredSettings[0];
            const validation: FieldValidation = {
                pattern: setting.pattern,
                maxLength: setting.size
            };
            if (!this.validationService.isStringFieldValid(validation, reference.reference)) {
                reference.reference = '';
            }
        });
    }
}
